import { ref, toRefs, computed } from 'vue'
import { defineStore } from 'pinia'
import * as userApi from '@/api/user'
import * as roleApi from '@/api/role'
import type { Router } from 'vue-router'
import type { MenuTreeListItem } from '@/api/role/types'
import { menuToMenuItems, sortMenuItems } from '@/utils/menuUtils'
import { useBreadcrumbStore } from './breadcrumb'
import { clearScreenTenantMemory } from '@/views/screen/shared/tenant/remember'

let routerPromise: Promise<Router> | null = null

async function getRouter() {
  if (!routerPromise) {
    routerPromise = import('@/router').then((mod) => mod.default)
  }
  return routerPromise
}

type LoginParams = {
  account: string
  password: string
}

type UserInfo = {
  tokenName: string // token名称
  tokenValue: string // token值
  isLogin: string // 是否已登录
  loginId: string // 此token对应的LoginId，未登录时为null
  loginType: string // 多账号体系下的账号类型
  tokenTimeout: string // token 剩余有效期（单位: 秒）
  sessionTimeout: string // Account-Session 剩余有效时间（单位: 秒）
  tokenSessionTimeout: string // Token-Session 剩余有效时间（单位: 秒）
  loginDeviceType: string // 登录设备类型
  tags: string // 自定义数据（暂无意义，留作扩展）
  phone: string // 手机号
  userId: number // userId
  account: string // 账户
  tenantId: string // 客户ID
  tenantName: string // 客户名称
  userType: string // 用户类型  超管 'admin' | 'manager' 其他客户管理员 'user_admin' ...
  tenantCode: string // 客户编码
}

type UserPerm = {
  userId: string
  roleIds: string[]
  path: string[]
  apiPath: string[]
  apiPerms: string[]
  menus: MenuTreeListItem[]
}

export const useUserStore = defineStore(
  'user',
  () => {
    const userInfo = ref<UserInfo>({
      tokenName: '',
      tokenValue: '',
      isLogin: '',
      loginId: '',
      loginType: '',
      tokenTimeout: '',
      sessionTimeout: '',
      tokenSessionTimeout: '',
      loginDeviceType: '',
      tags: '',
      phone: '',
      userId: 0,
      account: '',
      tenantId: '',
      tenantName: '',
      userType: '',
      tenantCode: '',
    })

    const userPrem = ref<UserPerm>({
      userId: '',
      roleIds: [],
      path: [],
      apiPath: [],
      apiPerms: [],
      menus: [],
    })

    const initRouter = async () => {
      const router = await getRouter()
      const sortedMenuItems = JSON.parse(localStorage.getItem('sortedMenuItems') || '[]')
      const redirectPath =
        sortedMenuItems.length > 0 ? sortedMenuItems[0]?.path || '/' : '/notFound'
      const redirectMeta =
        sortedMenuItems.length > 0
          ? {
              title: sortedMenuItems[0]?.meta?.title,
              isFullScreen: false,
              keepAlive: false,
              hidden: true,
              prem: true,
            }
          : {}
      router.addRoute({
        path: '/',
        name: 'home',
        redirect: redirectPath,
        meta: redirectMeta,
      })
      return redirectPath
    }

    // 登录
    function login(params: LoginParams) {
      return new Promise((resolve, reject) => {
        userApi
          .login(params)
          .then(async (res) => {
            console.log('🚀🚀🚀userStore Login res', res)
            userInfo.value = res.data
            resolve(res)

            // 等待权限数据加载完成
            try {
              await fetchUserPerm()
              console.log('权限数据加载完成')
              await initRouter()
              const router = await getRouter()
              router.replace({ path: '/' })
            } catch (error) {
              console.error('权限数据加载失败', error)
              const router = await getRouter()
              router.push({
                name: 'noPermission',
              })
            }
          })
          .catch((err) => {
            console.log('🚀🚀🚀userStore Login err', err)
            reject(err)
          })
      })
    }
    // 退出登录
    async function logout(active = false) {
      if (userInfo.value.tokenValue && active) {
        try {
          const res = await userApi.logout()
          console.log('✅ userStore logout success', res)
        } catch (error) {
          console.log('❌ userStore logout error', error)
        }
      }
      clearScreenTenantMemory(userInfo.value)
      userInfo.value = {
        tokenName: '',
        tokenValue: '',
        isLogin: '',
        loginId: '',
        loginType: '',
        tokenTimeout: '',
        sessionTimeout: '',
        tokenSessionTimeout: '',
        loginDeviceType: '',
        tags: '',
        phone: '',
        userId: 0,
        account: '',
        tenantId: '',
        tenantName: '',
        userType: '',
        tenantCode: '',
      }
      userPrem.value = {
        userId: '',
        roleIds: [],
        path: [],
        apiPath: [],
        apiPerms: [],
        menus: [],
      }
      localStorage.removeItem('sortedMenuItems')
      useBreadcrumbStore().clearRoutes()
      const router = await getRouter()
      const redirectPath = router.currentRoute.value.fullPath || ''
      console.log('✅ userStore Logout', redirectPath)
      if (router.currentRoute.value.name === 'login') return
      router.push({
        name: 'login',
        // query: {
        //   redirect: redirectPath,
        // },
      })
    }

    // 获取用户信息
    function getUserInfo() {
      return toRefs(userInfo.value)
    }

    const getMenuItems = (menus: MenuTreeListItem[] = userPrem.value.menus) => {
      return new Promise((resolve) => {
        const menuItems = menuToMenuItems(menus)
        const sortedItems = sortMenuItems(menuItems)
        localStorage.setItem('sortedMenuItems', JSON.stringify(sortedItems))
        resolve(sortedItems)
      })
    }

    function fetchUserPerm() {
      return new Promise((resolve, reject) => {
        roleApi
          .queryUserMenuPermission()
          .then(async (res) => {
            console.log('🚀🚀🚀userStore fetchUserPerm res', res)
            // 确保数据结构正确
            if (res.data) {
              userPrem.value = {
                userId: res.data.userId || '',
                roleIds: res.data.roleIds || [],
                path: res.data.path || [],
                apiPath: res.data.apiPath || [],
                apiPerms: res.data.apiPerms || [],
                menus: res.data.menus.filter((item) => item.children.length > 0) || [],
              }
              await getMenuItems()
              console.log('🚀🚀🚀userStore userPrem.value', userPrem.value)
            } else {
              console.warn('权限接口返回数据为空')
              userPrem.value = {
                userId: '',
                roleIds: [],
                path: [],
                apiPath: [],
                apiPerms: [],
                menus: [],
              }
              await getMenuItems()
            }
            resolve(res)
          })
          .catch((err) => {
            console.log('🚀🚀🚀userStore fetchUserPerm err', err)
            // 出错时设置默认值
            userPrem.value = {
              userId: '',
              roleIds: [],
              path: [],
              apiPath: [],
              apiPerms: [],
              menus: [],
            }
            reject(err)
          })
      })
    }

    // 权限判断计算属性
    /**
     * 是否为系统管理员 (超级管理员)
     * 包含：admin (超级管理员)、manager (后台管理员)
     */
    const isSuperAdmin = computed(() => {
      const type = userInfo.value.userType
      return type === 'admin' || type === 'manager'
    })
    // 判断是否为超管
    const isSuperManager = computed(() => {
      const type = userInfo.value.userType
      return type === 'admin'
    })
    /**
     * 是否为用户管理员
     * 包含：user_admin (用户超级管理员)、user_manager (用户管理员)
     */
    const isUserAdmin = computed(() => {
      const type = userInfo.value.userType
      return type === 'user_admin' || type === 'user_manager'
    })

    /**
     * 判断当前用户是否拥有指定按钮权限
     * @param perm 权限字符串，或权限字符串数组（需全部满足）
     */
    const hasPermission = (perm: string | string[]) => {
      const perms = userPrem.value.apiPerms
      if (typeof perm === 'string') {
        return perms.includes(perm)
      }
      return perm.length > 0 && perm.every((item) => perms.includes(item))
    }

    return {
      login,
      logout,
      getUserInfo,
      userInfo,
      userPrem,
      fetchUserPerm,
      getMenuItems,
      hasPermission,
      isSuperAdmin,
      isUserAdmin,
      isSuperManager,
    }
  },
  {
    persist: {
      key: `${import.meta.env.VITE_STOTRE_NAME}_USER_INFO`,
      storage: localStorage,
    },
  }
)
